import axios from 'axios';

// Bound every request so a provider outage cannot leave a page spinning forever.
axios.defaults.timeout = 15000;
axios.defaults.withCredentials = true;
let activeScope = null;
let scopeController = new AbortController();

export const setActiveScope = (scope) => {
  scopeController.abort();
  scopeController = new AbortController();
  activeScope = scope;
};

axios.interceptors.request.use((config) => {
  const csrfCookie = document.cookie
    .split('; ')
    .find((entry) => entry.startsWith('cloud_watcher_csrf='));
  if (csrfCookie) {
    config.headers = config.headers || {};
    config.headers['X-CSRF-Token'] = decodeURIComponent(csrfCookie.split('=').slice(1).join('='));
  }
  if (activeScope && !config.skipProjectScope) {
    config.headers = config.headers || {};
    config.headers['X-Organization-ID'] = activeScope.organizationId;
    config.headers['X-Project-ID'] = activeScope.projectId;
  }
  if (!config.signal) config.signal = scopeController.signal;
  return config;
});
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      window.dispatchEvent(new Event('cloud-watcher-session-expired'));
    }
    return Promise.reject(error);
  }
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
// If BACKEND_URL is empty, use relative path /api
// Otherwise, ensure it doesn't end with a slash before adding /api
const API = BACKEND_URL 
  ? (BACKEND_URL.endsWith('/') ? `${BACKEND_URL}api` : `${BACKEND_URL}/api`)
  : '/api';

export const getTerminalWebSocketUrl = (resourceId) => {
  const backendOrigin = BACKEND_URL ? BACKEND_URL.replace(/\/$/, '') : window.location.origin;
  return `${backendOrigin}/api`.replace(/^http/, 'ws') + `/resources/${encodeURIComponent(resourceId)}/terminal`;
};

const getAuthHeader = () => ({});
const organizationOnly = (organizationId) => ({
  skipProjectScope: true,
  headers: { 'X-Organization-ID': organizationId },
});

export const api = {
  // Generic methods
  get: (endpoint, config = {}) => axios.get(`${API}${endpoint}`, { headers: getAuthHeader(), ...config }),
  post: (endpoint, data = {}, config = {}) => axios.post(`${API}${endpoint}`, data, { headers: getAuthHeader(), ...config }),
  put: (endpoint, data = {}, config = {}) => axios.put(`${API}${endpoint}`, data, { headers: getAuthHeader(), ...config }),
  patch: (endpoint, data = {}, config = {}) => axios.patch(`${API}${endpoint}`, data, { headers: getAuthHeader(), ...config }),

  // Auth
  register: (data) => axios.post(`${API}/auth/register`, data),
  login: (data) => axios.post(`${API}/auth/login`, data),
  getSession: () => axios.get(`${API}/auth/me`),
  logout: () => axios.post(`${API}/auth/logout`),
  forgotPassword: (email) => axios.post(`${API}/auth/forgot-password`, { email }),
  resetPassword: (token, newPassword) => axios.post(`${API}/auth/reset-password`, { token, new_password: newPassword }),
  previewInvitation: (token) => axios.post(`${API}/auth/invitations/preview`, { token }, { skipProjectScope: true }),
  acceptInvitation: (data) => axios.post(`${API}/auth/invitations/accept`, data, { skipProjectScope: true }),
  changePassword: (currentPassword, newPassword) => axios.post(`${API}/auth/change-password`, { current_password: currentPassword, new_password: newPassword }),

  // Personal SSH identity and proof-of-possession device keys. Private keys
  // never cross this API boundary; registration accepts OpenSSH public keys only.
  getSshIdentity: (organizationId) => axios.get(`${API}/v1/organizations/${encodeURIComponent(organizationId)}/ssh-identity`, organizationOnly(organizationId)),
  createSshDeviceChallenge: (organizationId, publicKey) => axios.post(`${API}/v1/organizations/${encodeURIComponent(organizationId)}/ssh-identity/challenges`, { public_key: publicKey }, organizationOnly(organizationId)),
  registerSshDeviceKey: (organizationId, data) => axios.post(`${API}/v1/organizations/${encodeURIComponent(organizationId)}/ssh-identity/device-keys`, data, organizationOnly(organizationId)),
  renameSshDeviceKey: (organizationId, deviceKeyId, data) => axios.patch(`${API}/v1/organizations/${encodeURIComponent(organizationId)}/ssh-identity/device-keys/${encodeURIComponent(deviceKeyId)}`, data, organizationOnly(organizationId)),
  revokeSshDeviceKey: (organizationId, deviceKeyId, data) => axios.post(`${API}/v1/organizations/${encodeURIComponent(organizationId)}/ssh-identity/device-keys/${encodeURIComponent(deviceKeyId)}/revoke`, data, organizationOnly(organizationId)),

  // Validated organization/project scope
  getOrganizations: () => axios.get(`${API}/v1/organizations`),
  getProjects: (organizationId) => axios.get(`${API}/v1/organizations/${encodeURIComponent(organizationId)}/projects`),
  getMemberships: (organizationId) => axios.get(`${API}/v1/organizations/${encodeURIComponent(organizationId)}/memberships`),
  inviteMembership: (organizationId, data) => axios.post(`${API}/v1/organizations/${encodeURIComponent(organizationId)}/memberships`, data),
  getInvitations: (organizationId) => axios.get(`${API}/v1/organizations/${encodeURIComponent(organizationId)}/invitations`, organizationOnly(organizationId)),
  createInvitation: (organizationId, data) => axios.post(`${API}/v1/organizations/${encodeURIComponent(organizationId)}/invitations`, data, organizationOnly(organizationId)),
  resendInvitation: (organizationId, invitationId) => axios.post(`${API}/v1/organizations/${encodeURIComponent(organizationId)}/invitations/${encodeURIComponent(invitationId)}/resend`, {}, organizationOnly(organizationId)),
  revokeInvitation: (organizationId, invitationId) => axios.post(`${API}/v1/organizations/${encodeURIComponent(organizationId)}/invitations/${encodeURIComponent(invitationId)}/revoke`, {}, organizationOnly(organizationId)),
  updateMembership: (organizationId, userId, data) => axios.patch(`${API}/v1/organizations/${encodeURIComponent(organizationId)}/memberships/${encodeURIComponent(userId)}`, data),
  getProjectRoleBindings: (organizationId, projectId) => axios.get(`${API}/v1/organizations/${encodeURIComponent(organizationId)}/projects/${encodeURIComponent(projectId)}/role-bindings`),
  setProjectRoleBinding: (organizationId, projectId, userId, data) => axios.put(`${API}/v1/organizations/${encodeURIComponent(organizationId)}/projects/${encodeURIComponent(projectId)}/role-bindings/${encodeURIComponent(userId)}`, data),
  getEffectivePermissions: (organizationId, projectId, userId) => axios.get(`${API}/v1/organizations/${encodeURIComponent(organizationId)}/projects/${encodeURIComponent(projectId)}/members/${encodeURIComponent(userId)}/effective-permissions`),
  getResourceGrants: (organizationId, projectId, resourceId) => axios.get(`${API}/v1/organizations/${encodeURIComponent(organizationId)}/projects/${encodeURIComponent(projectId)}/resources/${encodeURIComponent(resourceId)}/grants`),
  createResourceGrant: (organizationId, projectId, resourceId, data, idempotencyKey) => axios.post(`${API}/v1/organizations/${encodeURIComponent(organizationId)}/projects/${encodeURIComponent(projectId)}/resources/${encodeURIComponent(resourceId)}/grants`, data, { headers: { 'Idempotency-Key': idempotencyKey } }),
  revokeResourceGrant: (organizationId, projectId, grantId, data) => axios.post(`${API}/v1/organizations/${encodeURIComponent(organizationId)}/projects/${encodeURIComponent(projectId)}/grants/${encodeURIComponent(grantId)}/revoke`, data),
  getMyWork: (organizationId, projectId, params = {}) => axios.get(`${API}/v1/organizations/${encodeURIComponent(organizationId)}/projects/${encodeURIComponent(projectId)}/my-work`, { params }),
  getRequisitionTimeline: (organizationId, projectId, requestId) => axios.get(`${API}/v1/organizations/${encodeURIComponent(organizationId)}/projects/${encodeURIComponent(projectId)}/requisitions/${encodeURIComponent(requestId)}/timeline`),
  decideRequisition: (organizationId, projectId, requestId, decision, data) => axios.post(`${API}/v1/organizations/${encodeURIComponent(organizationId)}/projects/${encodeURIComponent(projectId)}/requisitions/${encodeURIComponent(requestId)}/${decision}`, data),
  cancelRequisition: (organizationId, projectId, requestId, expectedRevision) => axios.post(`${API}/v1/organizations/${encodeURIComponent(organizationId)}/projects/${encodeURIComponent(projectId)}/requisitions/${encodeURIComponent(requestId)}/cancel`, { expected_revision: expectedRevision }),
  requestPower: (organizationId, projectId, resourceId, data, idempotencyKey) => axios.post(`${API}/v1/organizations/${encodeURIComponent(organizationId)}/projects/${encodeURIComponent(projectId)}/resources/${encodeURIComponent(resourceId)}/power-requests`, data, { headers: { 'Idempotency-Key': idempotencyKey } }),

  // Providers
  getProviders: () => axios.get(`${API}/providers`, { headers: getAuthHeader() }),
  getProviderTypes: () => axios.get(`${API}/providers/types`, { headers: getAuthHeader() }),
  createProvider: (data) => axios.post(`${API}/providers`, data, { headers: getAuthHeader() }),
  validateProvider: (data) => axios.post(`${API}/providers/validate`, data, { headers: getAuthHeader() }),
  deleteProvider: (id) => axios.delete(`${API}/providers/${id}`, { headers: getAuthHeader() }),
  discoverProviderResources: (id) => axios.post(`${API}/providers/${id}/discover`, {}, { headers: getAuthHeader() }),
  checkProviderCapabilities: (id) => axios.post(`${API}/providers/${id}/check-capabilities`, {}, { headers: getAuthHeader() }),
  syncAllProviderResources: (id) => axios.post(`${API}/providers/${id}/sync-all`, {}, { headers: getAuthHeader() }),
  attachExistingResource: (id, data) => axios.post(`${API}/providers/${id}/resources/attach`, data, { headers: getAuthHeader() }),

  // Resources
  getResources: (params = {}) => axios.get(`${API}/resources`, { params, headers: getAuthHeader() }),
  searchResources: ({ q, provider, status, type } = {}) =>
    axios.get(`${API}/resources`, {
      params: { q, provider, status, type },
      headers: getAuthHeader(),
    }),
  getResource: (id) => axios.get(`${API}/resources/${id}`, { headers: getAuthHeader() }),
  getResourceCapabilities: (id) => axios.get(`${API}/v1/resources/${id}/capabilities`, { headers: getAuthHeader() }),
  syncResource: (id) => axios.post(`${API}/resources/${id}/sync`, {}, { headers: getAuthHeader() }),
  getTerminalAccess: (id) => axios.get(`${API}/resources/${id}/terminal-access`, { headers: getAuthHeader() }),
  scanTerminalHostKey: (id, data) => axios.post(`${API}/resources/${id}/terminal-host-key`, data, { headers: getAuthHeader() }),
  saveTerminalProfile: (id, data) => axios.put(`${API}/resources/${id}/terminal-profile`, data, { headers: getAuthHeader() }),
  deleteTerminalProfile: (id) => axios.delete(`${API}/resources/${id}/terminal-profile`, { headers: getAuthHeader() }),
  createTerminalTicket: (id) => axios.post(`${API}/resources/${id}/terminal-ticket`, {}, { headers: getAuthHeader() }),

  // Metrics
  getMetrics: (id) => axios.get(`${API}/resources/${id}/metrics`, { headers: getAuthHeader() }),
  getMetricHistory: (id, startDate, endDate) => 
    axios.get(`${API}/resources/${id}/metric-history`, { 
      params: { start_date: startDate, end_date: endDate },
      headers: getAuthHeader() 
    }),

  // Health
  getHealthSummary: () => axios.get(`${API}/health/summary`, { headers: getAuthHeader() }),

  // Analytics
  getAnalyticsSummary: () => axios.get(`${API}/analytics/summary`, { headers: getAuthHeader() }),
  getCostHistory: (startDate, endDate, granularity = 'DAILY', providerId = null) => 
    axios.get(`${API}/analytics/cost-history`, { 
      params: { 
        start_date: startDate, 
        end_date: endDate, 
        granularity,
        ...(providerId && { provider_id: providerId })
      },
      headers: getAuthHeader() 
    }),
  getCostByProvider: () => axios.get(`${API}/analytics/cost-by-provider`, { headers: getAuthHeader() }),
  getCostForecast: (daysAhead = 30) => 
    axios.get(`${API}/analytics/cost-forecast`, { 
      params: { days_ahead: daysAhead },
      headers: getAuthHeader() 
    }),
  getAnomalyDetection: () => axios.get(`${API}/analytics/anomaly-detection`, { headers: getAuthHeader() }),

  // Budgets
  getBudgets: () => axios.get(`${API}/budgets`, { headers: getAuthHeader() }),
  getBudget: (id) => axios.get(`${API}/budgets/${id}`, { headers: getAuthHeader() }),
  createBudget: (data) => axios.post(`${API}/budgets`, data, { headers: getAuthHeader() }),
  deleteBudget: (id) => axios.delete(`${API}/budgets/${id}`, { headers: getAuthHeader() }),

  // Alerts
  getAlerts: (status = null) => 
    axios.get(`${API}/alerts`, { 
      params: status ? { status } : {},
      headers: getAuthHeader() 
    }),
  getAlertRules: () => axios.get(`${API}/alerts/rules`, { headers: getAuthHeader() }),
  createAlertRule: (data) => axios.post(`${API}/alerts/rules`, data, { headers: getAuthHeader() }),
  updateAlertStatus: (id, status) => 
    axios.patch(`${API}/alerts/${id}`, { status }, { headers: getAuthHeader() }),
  deleteAlertRule: (id) => axios.delete(`${API}/alerts/rules/${id}`, { headers: getAuthHeader() }),

  // Tags
  addResourceTags: (id, tags) => axios.post(`${API}/resources/${id}/tags`, tags, { headers: getAuthHeader() }),
  removeResourceTags: (id, tags) => axios.delete(`${API}/resources/${id}/tags`, { data: tags, headers: getAuthHeader() }),
};
